import { useRequest } from 'ahooks';
import {
  Button,
  Form,
  Input,
  Layout,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Dropdown, LinkButton } from 'components';
import LocaleContext, { currentLocale } from 'locales';
import ConfigContext from 'store/ConfigContext';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AiOutlineDown } from 'react-icons/ai';
import { changeStateOfApproval, getRequestList } from 'services/websocket/approval';
import { requestTypes } from 'utils/types';
import { getCurrentConfig } from 'services/mgmt';
import Detail from './Detail';

/**
 * 컴포넌트: 서비스 신청 버튼
 * @param {React.Dispatch<React.SetStateAction<{type: string}>>} setDetail 상세 페이지 상태 변경
 * @returns ReactNode
 */
function RequestButton({ setDetail, filteredRequestTypes }) {
  const { locale } = useContext(LocaleContext);

  return (
    <Dropdown
      overlay={
        <Menu>
          {filteredRequestTypes.map((el) => (
            <Menu.Item key={el}>
              <Button type="text" onClick={() => setDetail({ type: el })}>
                {locale.resource[el]}
              </Button>
            </Menu.Item>
          ))}
        </Menu>
      }
    >
      <Button>
        <span>{currentLocale['txt.request']}</span>
        <AiOutlineDown className="anticon" />
      </Button>
    </Dropdown>
  );
}

const getTagColor = (approval) => {
  if (approval === 'approval.state.processed') return 'success';
  if (approval === 'approval.state.waiting') return 'warning';
  if (approval === 'approval.state.processing') return 'processing';
  if (approval === 'approval.state.rejected') return 'error';
  if (approval === 'approval.state.cancelled') return 'default';
  return 'default';
};

// 신청 취소
async function onRequestCancel(ids, locale, onSuccess) {
  const modal = Modal.confirm({
    title: locale.resource['txt.cancel-request'],
    content: (
      <Typography.Text type="danger">
        {locale.resource['txt.irreversible-operation']}
      </Typography.Text>
    ),
    okText: locale.resource['txt.application-cancel'],
    cancelText: locale.resource['txt.close'],
    okType: 'danger',
    okButtonProps: { type: 'primary' },
    onOk: () => {
      modal.update((preConfig) => ({
        ...preConfig,
        cancelButtonProps: { disabled: true },
        okButtonProps: { loading: true },
      }));

      async function executor(resolve, reject) {
        const res = await changeStateOfApproval({
          ids,
          state: 'approval.state.cancelled',
        });
        return res.status === 'success'
          ? resolve('txt.operation-success')
          : reject(res.invalidParams[0].reason || 'txt.cancel-only-waiting');
      }

      new Promise(executor)
        .then((data) => {
          modal.destroy();
          message.success(locale.resource[data]);
          onSuccess();
        })
        .catch((error) => {
          modal.update((preConfig) => ({
            ...preConfig,
            title: locale.resource['txt.operation-failed'],
            content: locale.resource[error],
            cancelText: locale.resource['txt.close'],
            cancelButtonProps: { disabled: false },
            okButtonProps: { style: { display: 'none' } },
          }));
        });
      return true;
    },
  });
  return null;
}

/**
 * 컴포넌트: 서비스 신청
 * @returns ReactNode
 */
export default function Request(props) {
  const { location } = props;

  // states
  const [detail, setDetail] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [typeFilter, setTypeFilter] = useState(''); // 서비스 신청의 타입 필터, ''는 전체를 의미

  // refs
  const requestTableRef = useRef(null); // 서비스 신청 목록 테이블
  const [form] = Form.useForm();

  // hooks
  const { locale } = useContext(LocaleContext);
  const config = useContext(ConfigContext);
  const fetchList = useRequest(() => getRequestList(typeFilter), { refreshDeps: [typeFilter] }); // 로그인한 사용자의 서비스 신청 목록 조회
  const [filteredRequestTypes, setFilteredRequestTypes] = useState([]);
  const [backConf, setBackConf] = useState(undefined);

  useEffect(() => {
    getCurrentConfig().then((data) => {
      setBackConf(data);
      let type =
        config.siteName === 'lgensol-usa'
          ? ['approval.types.create-vd', 'approval.types.delete-vd', 'approval.types.reset-vd']
          : requestTypes;

      if (!data.networkDrive) {
        type = type.filter((el) => !el.includes('drive'));
      }

      setFilteredRequestTypes(type);
    });
  }, []);

  // variables

  // 상세 페이지 닫힐 때
  const onDetailClose = () => {
    setDetail(undefined);
    form?.resetFields();
    fetchList.runAsync(typeFilter);
  };

  // 컴포넌트: 테이블 상단 영역
  const TableTitle = () => (
    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
      <Space>
        <RequestButton setDetail={setDetail} filteredRequestTypes={filteredRequestTypes} />
        <Button
          disabled={!(selectedRowKeys.length > 0)}
          onClick={() =>
            onRequestCancel(selectedRowKeys, locale, () => {
              setSelectedRowKeys([]);
              fetchList.runAsync(typeFilter);
            })
          }
        >
          {locale.resource['txt.application-cancel']}
        </Button>
      </Space>
      <Input.Group compact>
        <Select
          defaultValue=""
          value={typeFilter}
          style={{ width: 200 }}
          options={[
            { label: locale.resource['txt.total'], value: '' },
            ...requestTypes.map((el) => ({
              label: locale.resource[el],
              value: el,
            })),
          ]}
          onSelect={(value) => setTypeFilter(value)}
        />
      </Input.Group>
    </Space>
  );

  return (
    <Layout.Content>
      <Table
        rowKey="id"
        size="middle"
        ref={requestTableRef}
        loading={fetchList.loading}
        dataSource={fetchList.data}
        title={() => <TableTitle />}
        columns={[
          {
            title: locale.resource['txt.contents'],
            dataIndex: 'type',
            width: 180,
            render: (text, record) => (
              <LinkButton onClick={() => setDetail(record)}>{locale.resource[text]}</LinkButton>
            ),
          },
          {
            title: locale.resource['txt.status'],
            dataIndex: 'state',
            width: 180,
            render: (text) => <Tag color={getTagColor(text)}>{locale.resource[text]}</Tag>,
          },
          {
            title: locale.resource['txt.reason'],
            dataIndex: 'reason',
            ellipsis: true,
          },
          {
            title: locale.resource['txt.requested-date'],
            dataIndex: 'applyDate',
            width: 180,
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
          },
        ]}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
      />
      <Detail
        id={detail?.id}
        type={detail?.type}
        onDetailClose={onDetailClose}
        form={form}
        onRequestCancel={onRequestCancel}
        location={location}
        backConf={backConf}
      />
    </Layout.Content>
  );
}
